import React from 'react';
import { Mountain, Award, Users, Leaf, Shield, MapPin, Phone, Mail } from 'lucide-react';
import { aboutUs, companyInfo } from '../data/mockData';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';

const AboutPage = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      {/* Hero */}
      <div className="relative h-[45vh] flex items-center">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1486870591958-9b9d0d1dda99?w=1920"
            alt="About Us"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-black/40" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16">
          <div className="flex items-center gap-3 mb-4">
            <Mountain className="h-8 w-8 text-emerald-400" />
            <span className="text-emerald-400 font-semibold">Our Story</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">{aboutUs.title}</h1>
          <p className="text-xl text-white/80 max-w-2xl">
            {aboutUs.tagline}
          </p>
        </div>
      </div>

      {/* Story Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="text-emerald-600 font-semibold text-sm uppercase tracking-wider">Our Journey</span>
            <h2 className="text-3xl font-bold text-gray-900 mt-2 mb-6">{aboutUs.experience}</h2>
            <div className="prose prose-lg text-gray-600">
              {aboutUs.story.split('\n\n').map((para, idx) => (
                <p key={idx} className="mb-4 leading-relaxed">{para}</p>
              ))}
            </div>
            <p className="text-gray-600 mt-4 leading-relaxed">{aboutUs.teamInfo}</p>
          </div>
          <div className="relative">
            <img
              src="/images/WhatsApp Image 2025-12-29 at 11.45.38 AM.jpeg"
              alt="Himalayan Mountains"
              className="rounded-3xl shadow-2xl"
            />
            <div className="absolute -bottom-6 -left-6 bg-emerald-600 text-white p-6 rounded-2xl shadow-xl">
              <p className="text-4xl font-bold">25+</p>
              <p className="text-emerald-100">Years Experience</p>
            </div>
          </div>
        </div>
      </div>

      {/* Achievements */}
      <div className="bg-emerald-900 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Award className="h-12 w-12 text-emerald-400 mx-auto mb-4" />
            <h2 className="text-3xl font-bold text-white mb-4">Our Achievements</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {aboutUs.achievements.map((achievement, idx) => (
              <div key={idx} className="bg-white/10 backdrop-blur-md rounded-2xl p-6 text-center">
                <p className="text-white/90 leading-relaxed">{achievement}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Values */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-12">
          <span className="text-emerald-600 font-semibold text-sm uppercase tracking-wider">What Drives Us</span>
          <h2 className="text-3xl font-bold text-gray-900 mt-2">Our Values</h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {aboutUs.values.map((value, idx) => {
            const icons = [Shield, Leaf, Users, Mountain];
            const Icon = icons[idx % icons.length];
            return (
              <div key={idx} className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow">
                <div className="w-14 h-14 bg-emerald-100 rounded-xl flex items-center justify-center mb-4">
                  <Icon className="h-7 w-7 text-emerald-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{value.title}</h3>
                <p className="text-gray-600">{value.description}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Founder */}
      <div className="bg-gray-100 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-3xl p-8 md:p-12 shadow-lg">
            <div className="grid md:grid-cols-3 gap-8 items-center">
              <div className="flex justify-center items-center">
                   <img src="/images/WhatsApp Image 2026-01-04 at 12.12.31 PM.jpeg" alt="Magic Mountain Adventures" className="h-40 w-40 object-contain"/>
              </div>
              <div className="md:col-span-2">
                <span className="text-emerald-600 font-semibold text-sm uppercase tracking-wider">Meet Our Founder</span>
                <h3 className="text-2xl font-bold text-gray-900 mt-2 mb-2">{aboutUs.founder.name}</h3>
                <p className="text-emerald-600 font-medium mb-4">{aboutUs.founder.role}</p>
                <p className="text-gray-600 leading-relaxed">{aboutUs.founder.bio}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Contact Info */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900">Get In Touch</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-8">
          <div className="bg-white rounded-2xl p-6 shadow-lg text-center">
            <div className="w-14 h-14 bg-emerald-100 rounded-xl flex items-center justify-center mx-auto mb-4">
              <Phone className="h-7 w-7 text-emerald-600" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">Phone</h3>
            <a href={`tel:${companyInfo.phone}`} className="text-emerald-600 hover:underline">
              {companyInfo.phone}
            </a>
          </div>
          <div className="bg-white rounded-2xl p-6 shadow-lg text-center">
            <div className="w-14 h-14 bg-emerald-100 rounded-xl flex items-center justify-center mx-auto mb-4">
              <Mail className="h-7 w-7 text-emerald-600" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">Email</h3>
            <a href={`mailto:${companyInfo.email}`} className="text-emerald-600 hover:underline break-all">
              {companyInfo.email}
            </a>
          </div>
          <div className="bg-white rounded-2xl p-6 shadow-lg text-center">
            <div className="w-14 h-14 bg-emerald-100 rounded-xl flex items-center justify-center mx-auto mb-4">
              <MapPin className="h-7 w-7 text-emerald-600" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">Address</h3>
            <p className="text-gray-600">{companyInfo.address}</p>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default AboutPage;
